/**
 * Season descriptions for color analysis
 * Provides personalized descriptions based on season and undertone
 */
import {
  legacyUndertoneForApi,
  parseUndertoneFromStorage,
} from '../utils/seasonUndertone';

export const getSeasonDescription = (
  season: string,
  undertone: string
): string => {
  const descriptions: { [key: string]: string } = {
    // Spring variations
    'Light Spring': 'Light Spring - fresh, bright and radiant',
    'True Spring': 'True Spring - vibrant, energetic and luminous',
    'Warm Spring': 'Warm Spring - bold, golden and radiant',
    'Bright Spring': 'Bright Spring - clear, vivid and dynamic',
    Spring: 'Light Spring - fresh, bright and radiant',

    // Summer variations
    'Light Summer': 'Light Summer - soft, delicate and elegant',
    'True Summer': 'True Summer - calm, cool and graceful',
    'Soft Summer': 'Soft Summer - calm, cool and graceful',
    'Cool Summer': 'Cool Summer - sophisticated, muted and refined',
    Summer: 'True Summer - calm, cool and graceful',

    // Autumn variations
    'Soft Autumn': 'Soft Autumn - warm, harmonious and balanced',
    'True Autumn': 'True Autumn - rich, warm and earthy',
    'Warm Autumn': 'Warm Autumn - deep, golden and radiant',
    'Deep Autumn': 'Deep Autumn - rich, warm and sophisticated',
    Autumn: 'Soft Autumn - warm, harmonious and balanced',

    // Winter variations
    'True Winter': 'True Winter - bold, clear and striking',
    'Cool Winter': 'Cool Winter - crisp, dramatic and elegant',
    'Bright Winter': 'Bright Winter - vibrant, bold and sophisticated',
    'Deep Winter': 'Deep Winter - rich, dramatic and powerful',
    Winter: 'True Winter - bold, clear and striking',

    // Neutral variations
    'True Neutral': 'True Neutral - versatile, balanced and adaptable',
    'Deep Neutral': 'Deep Neutral - bold, rich and dramatic',
    Neutral: 'True Neutral - versatile, balanced and adaptable',
  };

  // Try to find exact match first (case-insensitive)
  const seasonLower = season.trim();
  const exactMatch = Object.keys(descriptions).find(
    (key) => key.toLowerCase() === seasonLower.toLowerCase()
  );
  if (exactMatch) return descriptions[exactMatch];

  // Fallback to undertone-based descriptions
  const fallbackDescriptions: { [key: string]: { [key: string]: string } } = {
    warm: {
      spring: 'Light Spring - fresh, bright and radiant',
      summer: 'True Spring - vibrant, energetic and luminous',
      autumn: 'Warm Spring - bold, golden and radiant',
      winter: 'Warm Spring - bold, golden and radiant',
    },
    cool: {
      spring: 'Light Summer - soft, delicate and elegant',
      summer: 'True Summer - calm, cool and graceful',
      autumn: 'Cool Summer - sophisticated, muted and refined',
      winter: 'Cool Winter - crisp, dramatic and elegant',
    },
    neutral: {
      spring: 'Soft Autumn - warm, harmonious and balanced',
      summer: 'True Neutral - versatile, balanced and adaptable',
      autumn: 'Soft Autumn - warm, harmonious and balanced',
      winter: 'Deep Neutral - bold, rich and dramatic',
    },
  };

  const seasonKey = season.toLowerCase().trim() as
    | 'spring'
    | 'summer'
    | 'autumn'
    | 'winter';
  const undertoneGroup = legacyUndertoneForApi(
    parseUndertoneFromStorage(undertone)
  );
  return (
    fallbackDescriptions[undertoneGroup]?.[seasonKey] ||
    'Embark on your colour journey'
  );
};
